/**
 * useWoRowSave — the WO samples grid's row-save machinery, extracted verbatim
 * from WorkOrdersTabContent (600-line cap): optimistic save + server-snapshot
 * revert, the 409 signoff-reset confirm flow, and the OK-only "Change not
 * saved" dialog for rejected saves.
 */
import { useCallback, useRef, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import { CONFIRM_INITIAL, type ConfirmDialogState } from '../../components/shared/ConfirmDialog';
import { updateWorkOrderDetail } from '../../services/workOrderService';
import { formatTrimNote, trimFreedAnything, type ScheduleTrimCounts } from '../../utils/scheduleTrimNote';
import type { WorkOrderDetailWithRelations } from '../../types/workOrder';
import { buildDetailPayload, isSelfReview } from './workOrderDetailPayload';

// BUG-2026-0190: appended to the save snackbar when the same person is both
// Tested By and Reviewed By. Non-blocking — the save still succeeds.
const SELF_REVIEW_WARNING =
  'Heads up: the same person is set as both Tested By and Reviewed By on this test.';

interface SnackbarState {
  open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning';
}

export function useWoRowSave(opts: {
  details: WorkOrderDetailWithRelations[];
  setDetails: Dispatch<SetStateAction<WorkOrderDetailWithRelations[]>>;
  serverDetailsRef: MutableRefObject<Map<string, WorkOrderDetailWithRelations>>;
  confirmDlg: ConfirmDialogState;
  setConfirmDlg: (s: ConfirmDialogState) => void;
  setSnackbar: (s: SnackbarState) => void;
}) {
  const { setDetails, serverDetailsRef, confirmDlg, setConfirmDlg, setSnackbar } = opts;

  // BUG-2026-0209: the row currently parked behind a "Yes, save and reset"
  // prompt. ConfirmDialog fires onClose BEFORE onConfirm, so the close handler
  // can't tell cancel from confirm synchronously — onConfirm flips `confirmed`
  // and the close handler decides one microtask later (see handleConfirmClose).
  const pendingResetRef = useRef<{ key: string; confirmed: boolean } | null>(null);

  const persistRow = useCallback(async (
    key: string,
    payload: Record<string, unknown>,
    saveOpts: { confirmReset?: boolean } = {},
  ) => {
    const response = await updateWorkOrderDetail(key, payload, saveOpts);
    // G2-2 (audit #2): approving a test end-trims its remaining schedule days;
    // the advisory counts ride the response — keep them out of the row state.
    const { scheduleTrim, ...updated } = response as WorkOrderDetailWithRelations & {
      scheduleTrim?: ScheduleTrimCounts;
    };
    setDetails((prev) => prev.map((d) => d.WorkOrderDetailKey === key ? { ...d, ...updated } : d));
    // BUG-2026-0209: the PUT echoes the bare WorkOrderDetail row — no joined
    // Location / Sample / Depth / Type / Test Name columns. Merge it INTO the
    // snapshot instead of replacing it, otherwise a later revert (cancelled
    // reset prompt, rejected save) paints the row with those cells blank.
    const prevSnap = serverDetailsRef.current.get(key);
    serverDetailsRef.current.set(key, { ...prevSnap, ...updated } as WorkOrderDetailWithRelations);
    // BUG-2026-0190: soft, non-blocking warning when tester == reviewer. It
    // rides the same save snackbar (severity bumped to 'warning') so the save
    // still succeeds and the user is nudged, never blocked.
    const selfReview = isSelfReview(payload);
    if (trimFreedAnything(scheduleTrim)) {
      setSnackbar({
        open: true,
        message: `Saved — test approved, schedule updated: ${formatTrimNote(scheduleTrim)}.`
          + (selfReview ? ` ${SELF_REVIEW_WARNING}` : ''),
        severity: selfReview ? 'warning' : 'info',
      });
    } else {
      const base = saveOpts.confirmReset ? 'Saved — signoff reset' : 'Saved';
      setSnackbar({
        open: true,
        message: selfReview ? `${base}. ${SELF_REVIEW_WARNING}` : base,
        severity: selfReview ? 'warning' : 'success',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const revertRow = useCallback((key: string) => {
    const snap = serverDetailsRef.current.get(key);
    if (!snap) return;
    setDetails((prev) => prev.map((d) => d.WorkOrderDetailKey === key ? { ...snap } : d));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A failed save must be unmissable: the grid already showed the edit
  // optimistically, so a corner snackbar that auto-dismisses lets users walk
  // away believing e.g. an approval stuck when it was rejected. Revert the row
  // first, then force an OK-only dialog (hideCancel) they must acknowledge.
  const showSaveError = useCallback((key: string, message: string) => {
    revertRow(key);
    setConfirmDlg({
      open: true,
      title: 'Change not saved',
      message: `${message}\n\nThe row has been put back to its last saved values.`,
      confirmLabel: 'OK',
      confirmColor: 'error',
      hideCancel: true,
      onConfirm: () => {},
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revertRow]);

  const handleRowSave = useCallback(async (row: Record<string, unknown>) => {
    const key = String(row.WorkOrderDetailKey);
    if (!key) return;
    const payload = buildDetailPayload(row);
    try {
      await persistRow(key, payload);
    } catch (err: any) {
      // Lifecycle gate from backend: 409 TEST_REQUIRES_RESET. Confirm with
      // the user that peeling off the top signoff level is intended, then
      // retry with confirmReset=true.
      const resp = err?.response;
      if (resp?.status === 409 && resp?.data?.error === 'TEST_REQUIRES_RESET') {
        const { currentStatus, targetStatus, fieldsToBeCleared } = resp.data as {
          currentStatus: string; targetStatus: string | null; fieldsToBeCleared: string[];
        };
        const fieldList = (fieldsToBeCleared?.length ? fieldsToBeCleared : ['signoff fields']).join(', ');
        const message = targetStatus
          ? `This test is currently "${currentStatus}". Saving your change will drop it back to "${targetStatus}" and clear: ${fieldList}. Continue?`
          : `This test is currently "${currentStatus}". Saving your change will reset the signoff. Continue?`;
        const pending = { key, confirmed: false };
        pendingResetRef.current = pending;
        setConfirmDlg({
          open: true,
          title: currentStatus === 'Approved' ? 'Test is approved' : 'Test is reviewed',
          message,
          confirmLabel: 'Yes, save and reset',
          confirmColor: 'warning',
          onConfirm: async () => {
            // Runs synchronously after onClose (same click) — mark it before
            // handleConfirmClose's deferred cancel-check looks.
            pending.confirmed = true;
            try {
              await persistRow(key, payload, { confirmReset: true });
            } catch {
              showSaveError(key, 'The save failed after you confirmed the reset. Nothing was changed.');
            }
          },
        });
        // Cancelling the dialog reverts the optimistic row edit — see
        // handleConfirmClose below (wired to the ConfirmDialog onClose).
        return;
      }
      // Signoff sequence violation (BUG-2026-0011 follow-up #5): user tried to
      // fill ApprovedBy without ReviewedBy, or ReviewedBy without CheckedBy.
      // Signoff role gate (403): user's role can't change that signoff field.
      // Both revert the optimistic edit and block on an OK dialog.
      const isSignoffReject = (resp?.status === 400 && resp?.data?.error === 'SIGNOFF_SEQUENCE_ERROR')
        || (resp?.status === 403 && resp?.data?.error === 'SIGNOFF_PERMISSION_ERROR');
      if (isSignoffReject) {
        showSaveError(key, resp.data.message ?? 'This change violates the signoff rules.');
        return;
      }
      showSaveError(key, 'The change could not be saved (server or network error). Please try again.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistRow, showSaveError]);

  // Revert the pending row edit whenever a reset-confirm dialog is dismissed
  // WITHOUT confirming. We key off the "Yes, save and reset" label set above.
  //
  // BUG-2026-0209: ConfirmDialog calls onClose() and THEN onConfirm() for the
  // confirm button, so this handler also runs on "Yes, save and reset". The
  // old version scanned every row and reverted any that diverged from its
  // snapshot — which, after any earlier save left a relation-less snapshot,
  // blanked Location / Sample / Depth on the row (and on every other row saved
  // that session). Now: only the row behind the prompt is a candidate, and the
  // cancel-vs-confirm decision is deferred one microtask so onConfirm has had
  // its chance to flag the pending entry as confirmed.
  const handleConfirmClose = useCallback(() => {
    const isResetPrompt = confirmDlg.confirmLabel === 'Yes, save and reset';
    const pending = pendingResetRef.current;
    if (isResetPrompt && pending) {
      queueMicrotask(() => {
        if (!pending.confirmed) revertRow(pending.key);
        if (pendingResetRef.current === pending) pendingResetRef.current = null;
      });
    }
    setConfirmDlg(CONFIRM_INITIAL);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmDlg, revertRow]);

  return { handleRowSave, handleConfirmClose };
}
